import React, { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import DownloadApp from '@/components/shared/DownloadApp';
import { 
  ShoppingCart,
  Calendar,
  Users,
  Package,
  FileText,
  CreditCard,
  Bell,
  Smartphone,
  Clock,
  CheckCircle,
  ArrowRight,
  Star,
  Zap,
  TrendingUp,
  Shield,
  BarChart3,
  Settings,
  Truck,
  Search,
  Filter,
  MapPin,
  AlertCircle,
  Gauge,
  Wrench,
  DollarSign
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faMobile, faUser, faStickyNote, faBuilding } from '@fortawesome/free-solid-svg-icons';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
}

interface SolutionOption {
  title: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  highlighted?: boolean;
}

const TyreFitmentCentreSoftwarePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    interest: 'whitelabel',
    subject: '',
    message: ''
  });

  const features: Feature[] = [
    {
      icon: <ShoppingCart className="w-12 h-12" />,
      title: "eCommerce Tyre Shopping",
      description: "Complete online tyre store with advanced search and filtering",
      benefits: [
        "Search by vehicle make, model & year",
        "Filter by tyre size, brand & price",
        "Real-time stock availability",
        "Customer reviews & ratings",
        "Tyre comparison tool",
        "Special offers & promotions",
        "Mobile-responsive storefront",
        "Secure checkout process"
      ]
    },
    {
      icon: <Calendar className="w-12 h-12" />,
      title: "Online Booking & Scheduling",
      description: "Let customers book tyre fitting appointments 24/7",
      benefits: [
        "Real-time bay availability",
        "Service duration estimates",
        "Automated booking confirmations",
        "SMS & email reminders",
        "Easy rescheduling options",
        "Multi-service booking",
        "Preferred technician selection",
        "Queue management system"
      ]
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Customer Relationship Management",
      description: "Build loyalty with comprehensive customer data management",
      benefits: [
        "Complete customer profiles",
        "Vehicle & tyre history tracking",
        "Service interval reminders",
        "Tyre rotation scheduling",
        "Loyalty points program",
        "Targeted marketing campaigns",
        "Customer feedback collection",
        "Automated follow-ups"
      ]
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: "Tyre & Parts Inventory",
      description: "Smart inventory management for tyres and accessories",
      benefits: [
        "Multi-location stock tracking",
        "Low stock alerts & auto-reordering",
        "Barcode scanning integration",
        "Supplier catalog integration",
        "Seasonal tyre management",
        "Stock transfer between locations",
        "Tyre aging & rotation tracking",
        "Warranty management"
      ]
    },
    {
      icon: <Search className="w-12 h-12" />,
      title: "Tyre Finder & Recommendations",
      description: "Help customers find the perfect tyres for their vehicle",
      benefits: [
        "VIN-based tyre lookup",
        "License plate recognition",
        "OEM tyre specifications",
        "Alternative tyre suggestions",
        "Weather-based recommendations",
        "Budget-friendly options",
        "Performance comparisons",
        "Expert advice integration"
      ]
    },
    {
      icon: <Bell className="w-12 h-12" />,
      title: "Real-Time Service Notifications",
      description: "Keep customers informed throughout the fitting process",
      benefits: [
        "Push notifications on mobile app",
        "Email & SMS status updates",
        "Service progress tracking",
        "Completion time estimates",
        "Ready for pickup alerts",
        "Payment reminders",
        "Service feedback requests",
        "Promotional notifications"
      ]
    },
    {
      icon: <Gauge className="w-12 h-12" />,
      title: "Vehicle Inspection & Diagnostics",
      description: "Comprehensive tyre and vehicle safety checks",
      benefits: [
        "Digital tyre tread depth recording",
        "Wheel alignment checks",
        "Pressure monitoring reports",
        "Visual damage documentation",
        "Photo evidence capture",
        "Safety recommendations",
        "Service history logging",
        "Compliance reporting"
      ]
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: "Quotes & Invoicing",
      description: "Professional quotations and invoices in seconds",
      benefits: [
        "Instant tyre quotes",
        "Itemized invoicing",
        "Multiple tyre pricing tiers",
        "Labour cost calculations",
        "Tax compliance & VAT",
        "Discount management",
        "Package deal pricing",
        "Digital receipt delivery"
      ]
    },
    {
      icon: <CreditCard className="w-12 h-12" />,
      title: "Online Payment Processing",
      description: "Accept payments securely online and in-store",
      benefits: [
        "Credit/Debit card payments",
        "Mobile wallet support",
        "Split payment options",
        "Payment plans & financing",
        "Secure payment gateway",
        "Automated receipts",
        "Refund management",
        "Multi-currency support"
      ]
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: "Analytics & Business Intelligence",
      description: "Data-driven insights to grow your business",
      benefits: [
        "Sales performance tracking",
        "Popular tyre brand analysis",
        "Customer acquisition metrics",
        "Service bay utilization",
        "Revenue forecasting",
        "Inventory turnover reports",
        "Marketing ROI tracking",
        "Custom dashboard creation"
      ]
    },
    {
      icon: <Truck className="w-12 h-12" />,
      title: "Supplier & Supply Chain",
      description: "Streamline your tyre procurement process",
      benefits: [
        "Multiple supplier management",
        "Automated purchase orders",
        "Delivery tracking",
        "Price comparison tools",
        "Supplier performance metrics",
        "Returns & warranty claims",
        "Bulk ordering discounts",
        "Supply chain optimization"
      ]
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Warranty & Service Guarantees",
      description: "Manage tyre warranties and service guarantees",
      benefits: [
        "Warranty registration",
        "Claim processing",
        "Service guarantee tracking",
        "Manufacturer warranty integration",
        "Extended warranty options",
        "Warranty expiry alerts",
        "Claims history reporting",
        "Customer warranty portal"
      ]
    }
  ];

  const solutionOptions: SolutionOption[] = [
    {
      title: "Join CarSparesEzyFind",
      description: "List your tyre fitment centre on our platform",
      features: [
        "Access to thousands of tyre shoppers",
        "Website + Android, iPhone & Huawei apps",
        "Online tyre shopping & booking system",
        "Integrated payment processing",
        "Marketing & SEO support",
        "No setup fees - Start immediately",
        "24/7 technical support",
        "Analytics & sales dashboard"
      ],
      cta: "Join the Marketplace",
      ctaLink: "https://www.CarSparesEzyFind.co.za/list-business"
    },
    {
      title: "WhiteLabel Solution",
      description: "Your own branded tyre fitment platform",
      features: [
        "Fully branded website & mobile apps",
        "Android, iPhone & Huawei native apps",
        "Complete tyre fitment centre system",
        "All features listed above included",
        "Your own domain & branding",
        "48-hour setup guarantee",
        "Unlimited products & customers",
        "Dedicated account manager"
      ],
      cta: "Get Custom Quote",
      ctaLink: "#whitelabel-quote",
      highlighted: true
    }
  ];

  const serviceStages = [
    {
      stage: "Online Order Placed",
      icon: <ShoppingCart className="w-6 h-6" />,
      notification: "Order confirmation with booking details sent via email & push notification"
    },
    {
      stage: "Appointment Confirmed",
      icon: <CheckCircle className="w-6 h-6" />,
      notification: "SMS reminder 24 hours before appointment with service bay details"
    },
    {
      stage: "Vehicle Checked In",
      icon: <Clock className="w-6 h-6" />,
      notification: "Push notification: 'Your vehicle has been checked in for tyre fitting'"
    },
    {
      stage: "Tyre Inspection Complete",
      icon: <Gauge className="w-6 h-6" />,
      notification: "Email with tread depth measurements and wheel alignment report"
    },
    {
      stage: "Fitting in Progress",
      icon: <Settings className="w-6 h-6" />,
      notification: "Real-time updates: 'Tyres being fitted - Est. completion in 20 mins'"
    },
    {
      stage: "Balancing & Alignment",
      icon: <Wrench className="w-6 h-6" />,
      notification: "Status update: 'Wheel balancing and alignment in progress'"
    },
    {
      stage: "Quality Check Complete",
      icon: <Shield className="w-6 h-6" />,
      notification: "Final inspection photos and pressure readings sent to customer"
    },
    {
      stage: "Ready for Collection",
      icon: <Bell className="w-6 h-6" />,
      notification: "Push notification & SMS: 'Your vehicle is ready for collection!'"
    },
    {
      stage: "Payment Complete",
      icon: <CreditCard className="w-6 h-6" />,
      notification: "Digital invoice and warranty certificate sent via email"
    }
  ];

  const stats = [
    { value: "2,500+", label: "Tyre Fitment Centres" },
    { value: "250K+", label: "Monthly Tyre Sales" },
    { value: "99%", label: "Customer Satisfaction" },
    { value: "48hrs", label: "Setup Time" }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you within 24 hours with your custom quote.');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Head>
        <title>Tyre Fitment Centre Software - Online Shopping, Booking & Management | CarSparesEzyFind</title>
        <meta name="description" content="Complete tyre fitment centre software with eCommerce tyre shopping, online booking, CRM, inventory management, customer notifications, and mobile apps for Android, iPhone & Huawei." />
        <meta name="keywords" content="tyre fitment software, tyre shop management, online tyre sales, tyre booking system, automotive software, tyre ecommerce" />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-slate-800 via-slate-900 to-black text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-30"></div>
          <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Gauge className="w-4 h-4 mr-2 text-yellow-300" />
                <span className="text-sm font-semibold">Complete Tyre Fitment Centre Solution</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Accelerate Your <span className="text-yellow-300">Tyre Business</span>
              </h1>
              
              <p className="text-xl md:text-2xl mb-8 text-slate-200">
                eCommerce Tyre Shopping • Online Booking • CRM • Inventory • Customer Notifications • Payments
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a 
                  href="https://www.CarSparesEzyFind.co.za/list-business" 
                  className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center"
                >
                  Join Our Platform
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a 
                  href="#whitelabel-quote" 
                  className="bg-white hover:bg-gray-100 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center border-2 border-white"
                >
                  Get Your Own Platform
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-3xl font-bold text-yellow-300">{stat.value}</div>
                    <div className="text-sm text-slate-200 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Apps & eCommerce Highlight */}
        <section className="py-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
              <div className="flex items-center gap-4">
                <ShoppingCart className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">Online Tyre Shopping</h3>
                  <p className="text-blue-100">24/7 eCommerce Store</p>
                </div>
              </div>
              <div className="hidden md:block w-px h-16 bg-white/30"></div>
              <div className="flex items-center gap-4">
                <Smartphone className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">Mobile Apps</h3>
                  <p className="text-blue-100">Android, iPhone & Huawei</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Complete Tyre Fitment Centre Solution
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to run a modern, efficient tyre fitment centre
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-8 hover:shadow-2xl transition-all hover:border-slate-600 transform hover:-translate-y-2"
                >
                  <div className="text-slate-700 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* eCommerce Features Highlight */}
        <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <ShoppingCart className="w-16 h-16 text-yellow-300 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Powerful Online Tyre Shopping
                </h2>
                <p className="text-xl text-slate-300">
                  Let customers browse and buy tyres online 24/7
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: <Search className="w-8 h-8" />, title: "Smart Search", desc: "Search by vehicle, size, or brand" },
                  { icon: <Filter className="w-8 h-8" />, title: "Advanced Filters", desc: "Filter by price, performance, season" },
                  { icon: <Star className="w-8 h-8" />, title: "Reviews & Ratings", desc: "Customer reviews and ratings" },
                  { icon: <DollarSign className="w-8 h-8" />, title: "Best Price", desc: "Price comparison and deals" },
                  { icon: <Calendar className="w-8 h-8" />, title: "Book Fitting", desc: "Schedule installation instantly" },
                  { icon: <Shield className="w-8 h-8" />, title: "Warranty Info", desc: "Clear warranty details" },
                  { icon: <Truck className="w-8 h-8" />, title: "Delivery Options", desc: "Home delivery or fitment" },
                  { icon: <CreditCard className="w-8 h-8" />, title: "Secure Payment", desc: "Safe online checkout" }
                ].map((item, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all">
                    <div className="text-yellow-300 flex justify-center mb-3">
                      {item.icon}
                    </div>
                    <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                    <p className="text-slate-300 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Customer Journey Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Bell className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Real-Time Customer Updates
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Keep customers informed at every stage with automated notifications
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-slate-600 to-slate-800 hidden md:block"></div>

                {serviceStages.map((stage, index) => (
                  <div key={index} className="relative mb-8 md:ml-20">
                    <div className="absolute -left-20 top-2 hidden md:block">
                      <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-800 rounded-full flex items-center justify-center text-white shadow-lg">
                        {stage.icon}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-slate-600 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="md:hidden text-slate-700">{stage.icon}</div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{stage.stage}</h4>
                          <div className="flex items-start gap-2">
                            <Bell className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-600">{stage.notification}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Solution Options Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Solution
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join our established platform or get your own branded solution
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {solutionOptions.map((option, index) => (
                <div 
                  key={index}
                  className={`relative rounded-2xl p-8 ${
                    option.highlighted 
                      ? 'bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-2xl transform scale-105' 
                      : 'bg-white border-2 border-gray-200'
                  }`}
                >
                  {option.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                        RECOMMENDED
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-3xl font-bold mb-2 ${option.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {option.title}
                    </h3>
                    <p className={option.highlighted ? 'text-slate-300' : 'text-gray-600'}>
                      {option.description}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                          option.highlighted ? 'text-yellow-300' : 'text-green-500'
                        }`} />
                        <span className={option.highlighted ? 'text-slate-100' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href={option.ctaLink}
                    className={`block text-center px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 ${
                      option.highlighted
                        ? 'bg-yellow-400 hover:bg-yellow-300 text-gray-900'
                        : 'bg-slate-700 hover:bg-slate-800 text-white'
                    }`}
                  >
                    {option.cta}
                    <ArrowRight className="inline-block ml-2 w-5 h-5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WhiteLabel Quote Section */}
        <section id="whitelabel-quote" className="py-20 bg-gradient-to-br from-gray-900 to-slate-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <Zap className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Get Your WhiteLabel Tyre Platform
                </h2>
                <p className="text-xl text-slate-300">
                  Custom branded website & mobile apps - Ready in 48 hours!
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Fully branded website with your logo",
                      "Android, iPhone & Huawei mobile apps",
                      "Complete eCommerce tyre store",
                      "Online booking & scheduling system",
                      "CRM & customer management",
                      "Inventory & parts ordering",
                      "Invoicing & payment processing",
                      "Push notifications & email alerts",
                      "Analytics & reporting dashboard",
                      "Your own domain name",
                      "Unlimited tyres & customers",
                      "Supplier integration"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <Clock className="w-6 h-6 text-blue-400 mr-2" />
                    Quick Setup Process
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-slate-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</div>
                      <div>
                        <h4 className="font-bold mb-1">Submit Your Details</h4>
                        <p className="text-slate-300 text-sm">Fill out the form below with your business information</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-slate-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</div>
                      <div>
                        <h4 className="font-bold mb-1">Receive Custom Quote</h4>
                        <p className="text-slate-300 text-sm">Get a detailed proposal within 24 hours</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-slate-600 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</div>
                      <div>
                        <h4 className="font-bold mb-1">Launch Your Platform</h4>
                        <p className="text-slate-300 text-sm">Your branded solution goes live in 48 hours</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-yellow-400/20 border-2 border-yellow-400 rounded-lg">
                    <p className="font-bold text-yellow-300">
                      ⚡ Special Offer: First 10 tyre centres get 30% off!
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 md:p-12 text-gray-900">
                <h3 className="text-3xl font-bold text-center mb-8">Request Your Custom Quote</h3>
                
                <form onSubmit={handleSubmit}>
                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='name' 
                        placeholder='Enter name' 
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-slate-600 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faUser} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='flex flex-col md:flex-row md:gap-5'>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Phone *</label>
                      <div className='relative'>
                        <div className='flex'>
                          <input 
                            type='text' 
                            name='code' 
                            placeholder='27' 
                            value={'+27'} 
                            disabled 
                            className='form-control border border-gray-300 border-r-0 text-sm xl:text-[14px] 2xl:text-md h-10 px-3 w-[50px] rounded-l' 
                          />
                          <input 
                            type='text' 
                            name='phone' 
                            placeholder='Enter mobile number' 
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded-r focus:border-slate-600 focus:outline-none' 
                          />
                        </div>
                        <FontAwesomeIcon icon={faMobile} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Email *</label>
                      <div className='relative'>
                        <input 
                          type='email' 
                          name='email' 
                          placeholder='Enter email Id' 
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-slate-600 focus:outline-none' 
                        />
                        <FontAwesomeIcon icon={faEnvelope} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Tyre Fitment Centre Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='businessName' 
                        placeholder='Enter your tyre centre name' 
                        value={formData.businessName}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-slate-600 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faBuilding} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>I'm interested in *</label>
                    <select
                      name='interest'
                      value={formData.interest}
                      onChange={handleChange}
                      className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-slate-600 focus:outline-none'
                    >
                      <option value="whitelabel">WhiteLabel Solution</option>
                      <option value="join">Joining CarSparesEzyFind</option>
                      <option value="both">Both Options - Need More Info</option>
                    </select>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Subject</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='subject' 
                        placeholder='Subject' 
                        value={formData.subject}
                        onChange={handleChange}
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-slate-600 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faStickyNote} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Message</label>
                    <textarea 
                      name='message'
                      placeholder='Tell us about your tyre fitment centre and requirements' 
                      rows={5} 
                      value={formData.message}
                      onChange={handleChange}
                      className='form-control py-3 border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full px-3 rounded focus:border-slate-600 focus:outline-none'
                    />
                  </div>

                  <div className='text-right'>
                    <button 
                      type="submit"
                      className='bg-slate-700 text-white border border-slate-700 uppercase transition-all hover:bg-white hover:text-slate-700 px-10 py-3 rounded font-bold'
                    >
                      Get My Quote
                    </button>
                  </div>
                </form>

                <p className="text-center text-sm text-gray-600 mt-6">
                  🔒 Your information is secure. We'll contact you within 24 hours with a detailed quote.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Digitize Your Tyre Fitment Centre?
            </h2>
            <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Join thousands of successful tyre centres using our platform to boost sales and customer satisfaction
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://www.CarSparesEzyFind.co.za/list-business"
                className="bg-white hover:bg-gray-100 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Join CarSparesEzyFind
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a 
                href="#whitelabel-quote"
                className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Get WhiteLabel Solution
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </section>
      </div>
      <DownloadApp/>
    </>
  );
};

export default TyreFitmentCentreSoftwarePage;